import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import serverService from '../services/serverService';
import { getProjectApis } from '../data/projectApiRegistry';
import { getAiUsageStats, AI_PRICING } from '../services/aiCostService';

/**
 * Project Detail Page
 * 각 서브도메인 프로젝트의 상세 정보 및 실시간 상태를 표시
 */
const ProjectDetail = () => {
  const { projectName } = useParams();
  const [serviceStatus, setServiceStatus] = useState(null);
  const [logs, setLogs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logsLoading, setLogsLoading] = useState(false);
  const [showLogs, setShowLogs] = useState(false);
  const [restarting, setRestarting] = useState(false);

  // API 관련 상태
  const [apiUsage, setApiUsage] = useState(null);
  const [apiUsageLoading, setApiUsageLoading] = useState(false);
  const [apiPeriod, setApiPeriod] = useState(7);

  // AI 비용 관련 상태 (AI 프로젝트 전용)
  const [aiUsage, setAiUsage] = useState(null);
  const [aiUsageLoading, setAiUsageLoading] = useState(false);
  const [aiPeriod, setAiPeriod] = useState(30);

  // 프로젝트 정보
  const projectInfo = {
    main: {
      name: 'Main',
      subdomain: 'ilouli.com',
      description: '메인 플랫폼 (랜딩, 인증, 포털)',
      port: 4000,
      pm2: 'ilouli-main-api',
      tier: 'general',
      features: ['랜딩 페이지', '사용자 인증', '프로필 관리', '알림'],
      github: 'ilouli-main',
      icon: '🏠'
    },
    auth: {
      name: 'Auth',
      subdomain: 'auth.ilouli.com',
      description: '중앙 인증 서비스',
      port: 4001,
      pm2: 'ilouli-auth-api',
      tier: 'admin',
      features: ['OAuth (Google/Kakao)', 'JWT 발급', '사용자 DB', '토큰 검증'],
      github: 'ilouli-auth',
      icon: '🔐'
    },
    community: {
      name: 'Community',
      subdomain: 'community.ilouli.com',
      description: '커뮤니티 + 트렌드 분석 플랫폼',
      port: 4002,
      pm2: 'ilouli-community-api',
      tier: 'general',
      features: ['자유게시판', '공지사항', '뉴스/브리핑', 'YouTube 트렌드', '게임'],
      github: 'ilouli-community',
      icon: '💬'
    },
    stock: {
      name: 'Stock',
      subdomain: 'stock.ilouli.com',
      description: '주식 정보 및 차트 서비스',
      port: 4003,
      pm2: 'ilouli-stock',
      tier: 'subscriber',
      features: ['실시간 차트', '종목 검색', '관심종목', 'KIS API 연동'],
      github: 'ilouli-stock',
      icon: '📈'
    },
    ai: {
      name: 'AI',
      subdomain: 'ai.ilouli.com',
      description: 'AI 기반 콘텐츠 도구',
      port: 4004,
      pm2: 'ilouli-ai-api',
      tier: 'subscriber',
      features: ['스토리보드', '콘텐츠 도구', 'AI 채팅', '오디오 분석'],
      github: 'ilouli-ai',
      icon: '🤖'
    },
    family: {
      name: 'Family',
      subdomain: 'family.ilouli.com',
      description: '가족 전용 공간',
      port: 3006,
      pm2: 'ilouli-family-api',
      tier: 'family',
      features: ['가족 캘린더', '공유 앨범', 'Google Calendar 연동'],
      github: 'ilouli-family',
      icon: '👨‍👩‍👧‍👦'
    },
    lab: {
      name: 'Lab',
      subdomain: 'lab.ilouli.com',
      description: '실험/테스트/게임/파일관리 공간',
      port: 4007,
      pm2: 'ilouli-lab-api',
      tier: 'family',
      features: ['파일 업로드/관리', '고스톱(맞고)', '포커', '수도쿠', '메모리 게임', '틱택토', '테스트 존', '에셋 라이브러리'],
      github: 'ilouli-lab',
      icon: '🔬'
    },
    devteam: {
      name: 'DevTeam',
      subdomain: 'devteam.ilouli.com',
      description: 'AI 개발팀 운영 시스템',
      port: 4060,
      pm2: 'ilouli-devteam',
      tier: 'admin',
      features: ['칸반보드', '에이전트 관리', '기획 모드', 'Claude CLI'],
      github: 'ilouli-devteam',
      icon: '👨‍💻'
    },
    work: {
      name: 'Work',
      subdomain: 'work.ilouli.com',
      description: '3D 애니메이션 협업 플랫폼',
      port: 8001,
      pm2: 'ilouli-work-backend',
      tier: 'general',
      features: ['프로젝트 관리', '실시간 협업', '파일 공유', '리뷰'],
      github: 'ilouli-work',
      icon: '🎬'
    },
    openclaude: {
      name: 'Claude Terminal',
      subdomain: 'claude.ilouli.com',
      description: 'Claude Code 웹 터미널',
      port: 4100,
      pm2: 'ilouli-openclaude',
      tier: 'admin',
      features: ['웹 터미널', 'Claude CLI', '프로젝트 관리', 'tmux 세션'],
      github: 'ilouli-openclaude',
      icon: '🖥️'
    },
    stockanalysis: {
      name: 'Stock Analysis',
      subdomain: 'stockanalysis.ilouli.com',
      description: '뉴스 연쇄효과 종목 분석',
      port: 3031,
      pm2: 'ilouli-stockanalysis',
      tier: 'admin',
      features: ['뉴스 분석', '종목 인사이트', 'Claude CLI 분석', '텔레그램 알림'],
      github: 'ilouli-StockAnalysis',
      icon: '📊'
    },
    news: {
      name: 'News Insight',
      subdomain: 'news.ilouli.com',
      description: '뉴스 기반 주식시장 인사이트 파이프라인',
      port: 4008,
      pm2: 'ilouli-news-api',
      tier: 'general',
      features: ['뉴스 수집', 'AI 인사이트', '주식 연관 분석', '자동 파이프라인'],
      github: 'ilouli-news',
      icon: '📰'
    },
    markdown: {
      name: 'Markdown Blog',
      subdomain: 'markdown.ilouli.com',
      description: 'Bear 스타일 마크다운 에디터',
      port: 4009,
      pm2: 'ilouli-markdownblog-api',
      tier: 'admin',
      features: ['Bear 스타일 Live Preview', 'YouTube 영상 요약', '시한부 공유 링크', '문서 트리 구조'],
      github: 'ilouli-MarkdownBlog',
      icon: '📝'
    },
    nexus: {
      name: 'Nexus',
      subdomain: 'nexus.ilouli.com',
      description: '지식 그래프 플랫폼',
      port: 4010,
      pm2: 'ilouli-nexus',
      tier: 'admin',
      features: ['지식 그래프', 'Python/FastAPI', 'SQLite'],
      github: 'ilouli-nexus',
      icon: '🕸️'
    },
    invest: {
      name: 'Invest',
      subdomain: 'invest.ilouli.com',
      description: '국내+해외 통합 투자처 탐색 플랫폼',
      port: 4012,
      pm2: 'ilouli-invest-api',
      tier: 'subscriber',
      features: ['시장 데이터', '경제 지표', '투자처 비교', '종목 스크리너', 'OpenBB 연동'],
      github: 'ilouli-invest',
      icon: '💹'
    }
  };

  const project = useMemo(() => projectInfo[projectName], [projectName]);

  // 서비스 상태 로드
  const loadServiceStatus = useCallback(async () => {
    if (!project) return;

    try {
      const result = await serverService.getPM2Processes();
      if (result.success) {
        const pm2Process = result.data?.find(p => p.name === project.pm2);
        setServiceStatus(pm2Process || null);
      }
    } catch (err) {
      console.error('Failed to load service status:', err);
      setServiceStatus(null);
    }
    setLoading(false);
  }, [project]);

  // 로그 로드
  const loadLogs = async () => {
    if (!project) return;

    setLogsLoading(true);
    try {
      const result = await serverService.getPM2Logs(project.pm2);
      if (result.success) {
        setLogs(result.data?.logs || '로그가 없습니다.');
      }
    } catch (err) {
      console.error('Failed to load logs:', err);
      setLogs('로그를 불러올 수 없습니다.');
    }
    setLogsLoading(false);
    setShowLogs(true);
  };

  // 서비스 재시작
  const handleRestart = async () => {
    if (!project || restarting) return;

    if (!window.confirm(`${project.name} 서비스를 재시작하시겠습니까?`)) {
      return;
    }

    setRestarting(true);
    try {
      await serverService.restartPM2Process(project.pm2);
      // 재시작 후 상태 다시 로드
      setTimeout(() => {
        loadServiceStatus();
        setRestarting(false);
      }, 2000);
    } catch (err) {
      console.error('Failed to restart service:', err);
      alert('서비스 재시작에 실패했습니다.');
      setRestarting(false);
    }
  };

  // API 사용량 로드
  const loadApiUsage = useCallback(async () => {
    if (!projectName) return;

    setApiUsageLoading(true);
    try {
      const result = await serverService.getProjectApiUsage(projectName, apiPeriod);
      if (result.success) {
        setApiUsage(result.data);
      }
    } catch (err) {
      console.error('Failed to load API usage:', err);
      setApiUsage(null);
    }
    setApiUsageLoading(false);
  }, [projectName, apiPeriod]);

  // AI 사용량 로드 (AI 프로젝트 전용)
  const loadAiUsage = useCallback(async () => {
    if (projectName !== 'ai') return;

    setAiUsageLoading(true);
    try {
      const result = await getAiUsageStats(aiPeriod);
      if (result.success) {
        setAiUsage(result.data);
      }
    } catch (err) {
      console.error('Failed to load AI usage:', err);
      setAiUsage(null);
    }
    setAiUsageLoading(false);
  }, [projectName, aiPeriod]);

  useEffect(() => {
    loadServiceStatus();
    loadApiUsage();
    loadAiUsage();

    // 30초마다 자동 갱신
    const interval = setInterval(loadServiceStatus, 30000);
    return () => clearInterval(interval);
  }, [loadServiceStatus, loadApiUsage, loadAiUsage]);

  // 업타임 포맷팅
  const formatUptime = (uptime) => {
    if (!uptime) return '-';
    const now = Date.now();
    const diff = now - uptime;
    const days = Math.floor(diff / 86400000);
    const hours = Math.floor((diff % 86400000) / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);

    if (days > 0) return `${days}일 ${hours}시간`;
    if (hours > 0) return `${hours}시간 ${minutes}분`;
    return `${minutes}분`;
  };

  // 메모리 포맷팅
  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return '0 MB';
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(1)} MB`;
  };

  // 비용 포맷팅
  const formatCost = (cost) => {
    if (!cost || cost === 0) return '$0.00';
    if (cost < 0.01) return `$${cost.toFixed(4)}`;
    return `$${cost.toFixed(2)}`;
  };

  // 토큰 수 포맷팅
  const formatTokens = (tokens) => {
    if (!tokens || tokens === 0) return '0';
    if (tokens >= 1000000) return `${(tokens / 1000000).toFixed(1)}M`;
    if (tokens >= 1000) return `${(tokens / 1000).toFixed(1)}K`;
    return tokens.toString();
  };

  if (!project) {
    return (
      <div className="max-w-[1200px] mx-auto p-6">
        <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
          <h2 className="text-2xl font-bold text-[#1d1d1f] mb-2">프로젝트를 찾을 수 없습니다</h2>
          <p className="text-gray-600 mb-6">요청하신 프로젝트 "{projectName}"이(가) 존재하지 않습니다.</p>
          <Link to="/" className="text-blue-500 font-medium hover:underline">← 대시보드로 돌아가기</Link>
        </div>
      </div>
    );
  }

  const status = serviceStatus?.status || 'unknown';

  return (
    <div className="max-w-[1200px] mx-auto p-6 flex flex-col gap-6">
      {/* 헤더 */}
      <div className="bg-white rounded-xl p-6 border border-gray-200">
        <Link to="/" className="text-blue-500 text-sm font-medium hover:underline mb-4 inline-block">← 대시보드</Link>
        <div className="flex items-center gap-4 mb-3">
          <span className="text-4xl">{project.icon}</span>
          <h2 className="text-2xl font-bold text-[#1d1d1f] m-0">{project.name}</h2>
          <span className={`px-3 py-1 rounded-2xl text-sm font-semibold ${
            status === 'online' ? 'bg-green-100 text-green-600' :
            status === 'offline' ? 'bg-red-100 text-red-600' :
            'bg-amber-100 text-amber-600'
          }`}>
            {status === 'online' ? '온라인' : status === 'offline' ? '오프라인' : '확인중'}
          </span>
        </div>
        <p className="text-gray-600 mb-3">{project.description}</p>
        <a
          href={`https://${project.subdomain}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-500 font-medium hover:underline"
        >
          {project.subdomain} →
        </a>
      </div>

      {/* 실시간 서비스 상태 */}
      <div className="bg-white rounded-xl p-6 border border-gray-200">
        <div className="flex justify-between items-center mb-4 max-md:flex-col max-md:gap-3 max-md:items-start">
          <h3 className="text-lg font-bold text-[#1d1d1f] m-0">서비스 상태</h3>
          <div className="flex gap-2">
            <button
              className="px-4 py-2 bg-amber-100 text-amber-700 rounded-lg text-sm font-semibold transition-all hover:bg-amber-200 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleRestart}
              disabled={restarting || status !== 'online'}
            >
              {restarting ? '재시작 중...' : '재시작'}
            </button>
            <button
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-semibold transition-all hover:bg-gray-200 disabled:opacity-50"
              onClick={loadLogs}
              disabled={logsLoading}
            >
              {logsLoading ? '로딩...' : '로그 보기'}
            </button>
            <button className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg text-sm font-semibold transition-all hover:bg-blue-200" onClick={loadServiceStatus}>
              새로고침
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-8 text-gray-500">상태를 확인하는 중...</div>
        ) : (
          <div className="grid grid-cols-6 gap-4 max-lg:grid-cols-3 max-md:grid-cols-2">
            <div className="bg-gray-50 rounded-lg p-4 flex items-center gap-3">
              <span className={`w-3 h-3 rounded-full ${
                status === 'online' ? 'bg-green-500' :
                status === 'offline' ? 'bg-red-500' : 'bg-amber-500'
              }`}></span>
              <div>
                <span className="text-xs text-gray-500 block">상태</span>
                <span className="font-semibold text-[#1d1d1f]">
                  {status === 'online' ? '실행중' : status === 'offline' ? '중지됨' : '확인중'}
                </span>
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <span className="text-xs text-gray-500 block">PID</span>
              <span className="font-semibold text-[#1d1d1f]">{serviceStatus?.pid || '-'}</span>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <span className="text-xs text-gray-500 block">CPU</span>
              <span className="font-semibold text-[#1d1d1f]">{serviceStatus?.cpu || 0}%</span>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <span className="text-xs text-gray-500 block">메모리</span>
              <span className="font-semibold text-[#1d1d1f]">{formatBytes(serviceStatus?.memory)}</span>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <span className="text-xs text-gray-500 block">업타임</span>
              <span className="font-semibold text-[#1d1d1f]">{formatUptime(serviceStatus?.uptime)}</span>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <span className="text-xs text-gray-500 block">재시작 횟수</span>
              <span className="font-semibold text-[#1d1d1f]">{serviceStatus?.restarts || 0}회</span>
            </div>
          </div>
        )}
      </div>

      {/* 로그 뷰어 */}
      {showLogs && (
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-bold text-[#1d1d1f] m-0">최근 로그</h3>
            <button className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-200" onClick={() => setShowLogs(false)}>닫기</button>
          </div>
          <div className="bg-[#1d1d1f] rounded-lg p-4 max-h-[400px] overflow-auto">
            <pre className="text-[13px] font-mono text-gray-300 whitespace-pre-wrap m-0">{logs}</pre>
          </div>
        </div>
      )}

      {/* 서비스 정보 */}
      <div className="bg-white rounded-xl p-6 border border-gray-200">
        <h3 className="text-lg font-bold text-[#1d1d1f] m-0 mb-4">서비스 정보</h3>
        <div className="grid grid-cols-4 gap-4 max-lg:grid-cols-2">
          <div className="bg-gray-50 rounded-lg p-4">
            <span className="text-xs text-gray-500 block mb-1">포트</span>
            <span className="font-semibold text-[#1d1d1f]">{project.port}</span>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <span className="text-xs text-gray-500 block mb-1">PM2 프로세스</span>
            <span className="font-mono text-sm bg-gray-200 px-2 py-0.5 rounded">{project.pm2}</span>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <span className="text-xs text-gray-500 block mb-1">접근 권한</span>
            <span className={`px-2 py-1 rounded text-xs font-semibold ${
              project.tier === 'general' ? 'bg-gray-200 text-gray-700' :
              project.tier === 'subscriber' ? 'bg-blue-100 text-blue-700' :
              project.tier === 'family' ? 'bg-purple-100 text-purple-700' :
              'bg-red-100 text-red-700'
            }`}>
              {project.tier === 'general' ? '일반' :
               project.tier === 'subscriber' ? '구독자' :
               project.tier === 'family' ? '가족' : '관리자'}
            </span>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <span className="text-xs text-gray-500 block mb-1">GitHub</span>
            <span className="font-mono text-sm bg-gray-200 px-2 py-0.5 rounded">{project.github}</span>
          </div>
        </div>
      </div>

      {/* 주요 기능 */}
      <div className="bg-white rounded-xl p-6 border border-gray-200">
        <h3 className="text-lg font-bold text-[#1d1d1f] m-0 mb-4">주요 기능</h3>
        <div className="flex flex-wrap gap-2">
          {project.features.map((feature, index) => (
            <span key={index} className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium">{feature}</span>
          ))}
        </div>
      </div>

      {/* API 엔드포인트 */}
      {(() => {
        const projectApis = getProjectApis(projectName);
        if (!projectApis) return null;

        return (
          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <h3 className="text-lg font-bold text-[#1d1d1f] m-0 mb-4">API 엔드포인트 ({projectApis.endpoints.length}개)</h3>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse min-w-[600px]">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase">메서드</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase">경로</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase">이름</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase">설명</th>
                  </tr>
                </thead>
                <tbody>
                  {projectApis.endpoints.map((endpoint, index) => (
                    <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded text-xs font-bold font-mono ${
                          endpoint.method === 'GET' ? 'bg-blue-100 text-blue-700' :
                          endpoint.method === 'POST' ? 'bg-green-100 text-green-700' :
                          endpoint.method === 'PUT' ? 'bg-amber-100 text-amber-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {endpoint.method}
                        </span>
                      </td>
                      <td className="py-3 px-4 font-mono text-sm text-gray-700">{endpoint.path}</td>
                      <td className="py-3 px-4 text-sm font-medium text-[#1d1d1f]">{endpoint.name}</td>
                      <td className="py-3 px-4 text-sm text-gray-600">{endpoint.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {/* API 사용량 통계 */}
      <div className="bg-white rounded-xl p-6 border border-gray-200">
        <div className="flex justify-between items-center mb-4 max-md:flex-col max-md:gap-3 max-md:items-start">
          <h3 className="text-lg font-bold text-[#1d1d1f] m-0">API 사용량</h3>
          <div className="flex gap-2">
            <select
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white cursor-pointer focus:outline-none focus:border-blue-500"
              value={apiPeriod}
              onChange={(e) => setApiPeriod(Number(e.target.value))}
            >
              <option value={7}>최근 7일</option>
              <option value={14}>최근 14일</option>
              <option value={30}>최근 30일</option>
            </select>
            <button className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg text-sm font-semibold hover:bg-blue-200 disabled:opacity-50" onClick={loadApiUsage} disabled={apiUsageLoading}>
              {apiUsageLoading ? '로딩...' : '새로고침'}
            </button>
          </div>
        </div>

        {apiUsageLoading ? (
          <div className="text-center py-8 text-gray-500">API 사용량을 불러오는 중...</div>
        ) : apiUsage ? (
          <>
            {/* 요약 카드 */}
            <div className="grid grid-cols-4 gap-4 mb-6 max-md:grid-cols-2">
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <span className="text-2xl font-bold text-[#1d1d1f] block">{apiUsage.summary?.total_calls || 0}</span>
                <span className="text-sm text-gray-500">총 호출</span>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <span className="text-2xl font-bold text-green-600 block">{apiUsage.summary?.success_rate || 100}%</span>
                <span className="text-sm text-gray-500">성공률</span>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <span className="text-2xl font-bold text-[#1d1d1f] block">{apiUsage.summary?.avg_response_time || 0}ms</span>
                <span className="text-sm text-gray-500">평균 응답시간</span>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <span className="text-2xl font-bold text-[#1d1d1f] block">{apiUsage.summary?.unique_users || 0}</span>
                <span className="text-sm text-gray-500">사용자 수</span>
              </div>
            </div>

            {/* 일별 추이 차트 */}
            {apiUsage.daily && apiUsage.daily.length > 0 && (
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">일별 호출 추이</h4>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={apiUsage.daily}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#737373' }} tickFormatter={(value) => value.slice(5)} />
                    <YAxis tick={{ fontSize: 11, fill: '#737373' }} />
                    <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }} formatter={(value, name) => [value, name === 'total_calls' ? '총 호출' : name === 'success_calls' ? '성공' : name]} labelFormatter={(label) => `날짜: ${label}`} />
                    <Line type="monotone" dataKey="total_calls" stroke="#3B82F6" strokeWidth={2} dot={{ fill: '#3B82F6', r: 3 }} name="총 호출" />
                    <Line type="monotone" dataKey="success_calls" stroke="#22C55E" strokeWidth={2} dot={{ fill: '#22C55E', r: 3 }} name="성공" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* 엔드포인트별 사용량 */}
            {apiUsage.endpoints && apiUsage.endpoints.length > 0 && (
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">엔드포인트별 사용량</h4>
                <div className="flex flex-col gap-2">
                  {apiUsage.endpoints.slice(0, 10).map((ep, index) => (
                    <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg max-md:flex-col max-md:gap-2 max-md:items-start">
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded text-xs font-bold font-mono ${
                          ep.method?.toUpperCase() === 'GET' ? 'bg-blue-100 text-blue-700' :
                          ep.method?.toUpperCase() === 'POST' ? 'bg-green-100 text-green-700' :
                          ep.method?.toUpperCase() === 'PUT' ? 'bg-amber-100 text-amber-700' :
                          'bg-red-100 text-red-700'
                        }`}>{ep.method}</span>
                        <span className="text-sm text-gray-700 font-mono">{ep.endpoint}</span>
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-gray-600">{ep.total_calls}회</span>
                        <span className={`font-semibold ${ep.success_rate >= 95 ? 'text-green-600' : ep.success_rate >= 80 ? 'text-amber-600' : 'text-red-600'}`}>{ep.success_rate}%</span>
                        <span className="text-gray-500">{ep.avg_response_time}ms</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 최근 에러 */}
            {apiUsage.recentErrors && apiUsage.recentErrors.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3">최근 에러 ({apiUsage.recentErrors.length}건)</h4>
                <div className="flex flex-col gap-2">
                  {apiUsage.recentErrors.map((error, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 bg-red-50 rounded-lg text-sm">
                      <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded font-bold">{error.status_code}</span>
                      <span className="text-gray-700 flex-1">{error.method} {error.endpoint}</span>
                      <span className="text-gray-500 text-xs">
                        {new Date(error.created_at).toLocaleString('ko-KR', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-8 text-gray-500">API 사용량 데이터가 없습니다.</div>
        )}
      </div>

      {/* AI 비용/사용량 섹션 (AI 프로젝트 전용) */}
      {projectName === 'ai' && (
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex justify-between items-center mb-4 max-md:flex-col max-md:gap-3 max-md:items-start">
            <h3 className="text-lg font-bold text-[#1d1d1f] m-0">AI 비용/사용량</h3>
            <div className="flex gap-2">
              <select
                className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white cursor-pointer focus:outline-none focus:border-blue-500"
                value={aiPeriod}
                onChange={(e) => setAiPeriod(Number(e.target.value))}
              >
                <option value={7}>최근 7일</option>
                <option value={14}>최근 14일</option>
                <option value={30}>최근 30일</option>
              </select>
              <button className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg text-sm font-semibold hover:bg-blue-200 disabled:opacity-50" onClick={loadAiUsage} disabled={aiUsageLoading}>
                {aiUsageLoading ? '로딩...' : '새로고침'}
              </button>
            </div>
          </div>

          {aiUsageLoading ? (
            <div className="text-center py-8 text-gray-500">AI 사용량을 불러오는 중...</div>
          ) : aiUsage ? (
            <>
              {/* AI 비용 요약 카드 */}
              <div className="grid grid-cols-4 gap-4 mb-6 max-md:grid-cols-2">
                <div className="bg-emerald-50 rounded-lg p-4 flex items-center gap-3">
                  <span className="text-2xl">💰</span>
                  <div>
                    <span className="text-xl font-bold text-emerald-600 block">{formatCost(aiUsage.summary?.total_cost || 0)}</span>
                    <span className="text-sm text-gray-500">총 비용</span>
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 flex items-center gap-3">
                  <span className="text-2xl">📤</span>
                  <div>
                    <span className="text-xl font-bold text-[#1d1d1f] block">{formatTokens(aiUsage.summary?.total_input_tokens || 0)}</span>
                    <span className="text-sm text-gray-500">입력 토큰</span>
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 flex items-center gap-3">
                  <span className="text-2xl">📥</span>
                  <div>
                    <span className="text-xl font-bold text-[#1d1d1f] block">{formatTokens(aiUsage.summary?.total_output_tokens || 0)}</span>
                    <span className="text-sm text-gray-500">출력 토큰</span>
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 flex items-center gap-3">
                  <span className="text-2xl">🔢</span>
                  <div>
                    <span className="text-xl font-bold text-[#1d1d1f] block">{aiUsage.summary?.total_requests || 0}</span>
                    <span className="text-sm text-gray-500">총 요청</span>
                  </div>
                </div>
              </div>

              {/* 제공업체별 사용량 */}
              {aiUsage.by_provider && aiUsage.by_provider.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">제공업체별 사용량</h4>
                  <div className="grid grid-cols-3 gap-4 max-md:grid-cols-1">
                    {aiUsage.by_provider.map((provider, index) => {
                      const providerInfo = AI_PRICING[provider.provider?.toLowerCase()];
                      return (
                        <div key={index} className="bg-gray-50 rounded-lg p-4">
                          <div className="flex justify-between items-center mb-3">
                            <span className="font-semibold text-[#1d1d1f]">{providerInfo?.name || provider.provider}</span>
                            <span className={`px-2 py-0.5 rounded text-xs font-semibold ${providerInfo?.tier === 'free' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                              {providerInfo?.tier === 'free' ? '무료' : '유료'}
                            </span>
                          </div>
                          <div className="grid grid-cols-3 gap-2 text-center">
                            <div>
                              <span className="text-lg font-bold text-[#1d1d1f] block">{provider.request_count || 0}</span>
                              <span className="text-xs text-gray-500">요청</span>
                            </div>
                            <div>
                              <span className="text-lg font-bold text-[#1d1d1f] block">{formatTokens(provider.total_tokens || 0)}</span>
                              <span className="text-xs text-gray-500">토큰</span>
                            </div>
                            <div>
                              <span className="text-lg font-bold text-emerald-600 block">{formatCost(provider.estimated_cost || 0)}</span>
                              <span className="text-xs text-gray-500">비용</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* 일별 비용 추이 차트 */}
              {aiUsage.daily && aiUsage.daily.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">일별 비용 추이</h4>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={aiUsage.daily}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#737373' }} tickFormatter={(value) => value.slice(5)} />
                      <YAxis tick={{ fontSize: 11, fill: '#737373' }} tickFormatter={(value) => `$${value}`} />
                      <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }} formatter={(value, name) => [`$${Number(value).toFixed(4)}`, name === 'cost' ? '비용' : name]} labelFormatter={(label) => `날짜: ${label}`} />
                      <Bar dataKey="cost" fill="#10B981" name="비용" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* 기능별 사용량 */}
              {aiUsage.by_feature && aiUsage.by_feature.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">기능별 사용량</h4>
                  <div className="flex flex-col gap-2">
                    {aiUsage.by_feature.slice(0, 10).map((feature, index) => (
                      <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg max-md:flex-col max-md:gap-2 max-md:items-start">
                        <span className="font-medium text-[#1d1d1f]">{feature.feature || '기타'}</span>
                        <div className="flex items-center gap-4 text-sm">
                          <span className="text-gray-600">{feature.request_count || 0}회</span>
                          <span className="text-gray-500">{formatTokens(feature.total_tokens || 0)} 토큰</span>
                          <span className="font-semibold text-emerald-600">{formatCost(feature.estimated_cost || 0)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-8 text-gray-500">AI 사용량 데이터가 없습니다.</div>
          )}
        </div>
      )}

      {/* 빠른 링크 */}
      <div className="bg-white rounded-xl p-6 border border-gray-200">
        <h3 className="text-lg font-bold text-[#1d1d1f] m-0 mb-4">빠른 링크</h3>
        <div className="grid grid-cols-3 gap-4 max-md:grid-cols-1">
          <a
            href={`https://${project.subdomain}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors no-underline"
          >
            <span className="text-2xl">🌐</span>
            <span className="font-medium text-[#1d1d1f]">사이트 방문</span>
          </a>
          <Link to="/server/monitor" className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors no-underline">
            <span className="text-2xl">🖥️</span>
            <span className="font-medium text-[#1d1d1f]">시스템 모니터링</span>
          </Link>
          <Link to="/server/database" className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors no-underline">
            <span className="text-2xl">🗄️</span>
            <span className="font-medium text-[#1d1d1f]">데이터베이스</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ProjectDetail;
